import { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import GeocodingService, {
  GeocodingResult,
} from "../../../api/services/geocoding.service";
import { useDebounce } from "../../../hooks/useDebounce";
import { useTranslation } from "react-i18next";
import styles from "./MapLocationPicker.module.scss";

// Fix for default marker icon in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface MapLocationPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  cityName?: string | null;
  countryName?: string | null;
  onLocationChange: (lat: number, lng: number, address: string) => void;
  disabled?: boolean;
}

interface MapClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void;
  disabled?: boolean;
}

// Component to handle map clicks
function MapClickHandler({ onMapClick, disabled }: MapClickHandlerProps) {
  useMapEvents({
    click: (e) => {
      if (!disabled) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

// Component to update map view when center/zoom changes
function MapViewUpdater({
  center,
  zoom,
}: {
  center: [number, number];
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [map, center, zoom]);

  return null;
}

const MapLocationPicker = ({
  latitude,
  longitude,
  address: initialAddress,
  cityName,
  countryName,
  onLocationChange,
  disabled = false,
}: MapLocationPickerProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
  } | null>(
    latitude && longitude
      ? {
          lat: latitude,
          lng: longitude,
          address: initialAddress || "",
        }
      : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    latitude && longitude ? [latitude, longitude] : [40.4093, 49.8671], // Default: Baku, Azerbaijan
  );
  const [mapZoom, setMapZoom] = useState(13);
  const [mapBounds, setMapBounds] = useState<
    [[number, number], [number, number]] | null
  >(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(searchQuery, 500);

  // Update map center and bounds when city changes
  useEffect(() => {
    if (cityName) {
      setIsLoading(true);
      GeocodingService.getCityBounds(cityName, countryName || undefined)
        .then((cityData) => {
          if (cityData) {
            setMapBounds(cityData.bounds);
            // Only set map center to city if no location is currently selected
            if (!selectedLocation) {
              setMapCenter(cityData.center);
              setMapZoom(13);
            }
          }
        })
        .catch((error) => {
          console.error("City bounds error:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Reset bounds when no city is selected
      setMapBounds(null);
    }
  }, [cityName, countryName, selectedLocation]);

  // Search for addresses when query changes
  useEffect(() => {
    if (debouncedQuery.trim().length >= 3) {
      setIsLoading(true);
      GeocodingService.searchAddress(
        debouncedQuery,
        5,
        cityName || undefined,
        countryName || undefined,
      )
        .then((results) => {
          setSearchResults(results);
          setShowResults(true);
        })
        .catch((error) => {
          console.error("Search error:", error);
          setSearchResults([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [debouncedQuery, cityName, countryName]);

  // Handle clicking outside search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if coordinates are within bounds
  const isWithinBounds = useCallback(
    (lat: number, lng: number): boolean => {
      if (!mapBounds) return true; // No bounds restriction
      const [[minLat, minLng], [maxLat, maxLng]] = mapBounds;
      return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
    },
    [mapBounds],
  );

  // Handle selecting a search result
  const handleSelectResult = useCallback(
    async (result: GeocodingResult) => {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);

      // Check if selected location is within city bounds
      if (!isWithinBounds(lat, lng)) {
        // Location is outside city bounds, don't allow selection
        return;
      }

      const address = GeocodingService.formatAddress(result);

      setSelectedLocation({ lat, lng, address });
      setMapCenter([lat, lng]);
      setMapZoom(15);
      setSearchQuery("");
      setShowResults(false);
      onLocationChange(lat, lng, address);
    },
    [onLocationChange, isWithinBounds],
  );

  // Handle map click
  const handleMapClick = useCallback(
    async (lat: number, lng: number) => {
      // Check if clicked location is within city bounds
      if (!isWithinBounds(lat, lng)) {
        // Location is outside city bounds, don't allow selection
        return;
      }

      setIsLoading(true);
      const reverseResult = await GeocodingService.reverseGeocode(lat, lng);

      if (reverseResult) {
        const address = GeocodingService.formatAddress(reverseResult);
        setSelectedLocation({ lat, lng, address });
        onLocationChange(lat, lng, address);
      } else {
        // If reverse geocoding fails, still set the location with coordinates
        const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        setSelectedLocation({ lat, lng, address });
        onLocationChange(lat, lng, address);
      }

      setIsLoading(false);
    },
    [onLocationChange, isWithinBounds],
  );

  // Initialize location from props
  useEffect(() => {
    if (latitude && longitude && !selectedLocation) {
      const address = initialAddress || "";
      setSelectedLocation({ lat: latitude, lng: longitude, address });
      setMapCenter([latitude, longitude]);
    }
  }, [latitude, longitude, initialAddress, selectedLocation]);

  // Handle getting current location
  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by your browser");
      return;
    }

    setIsGettingLocation(true);

    // Use watchPosition for better reliability, then clear it after getting position
    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        // Clear the watch after getting position
        navigator.geolocation.clearWatch(watchId);

        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        console.log("Got location:", lat, lng);

        // Check if location is within city bounds (if city is selected)
        if (!isWithinBounds(lat, lng)) {
          setIsGettingLocation(false);
          console.warn(
            `Location outside city bounds: ${cityName || "selected city"}`,
          );
          return;
        }

        // Get address for the current location
        setIsLoading(true);
        try {
          const reverseResult = await GeocodingService.reverseGeocode(lat, lng);

          if (reverseResult) {
            const address = GeocodingService.formatAddress(reverseResult);
            setSelectedLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
            setMapZoom(15);
            onLocationChange(lat, lng, address);
          } else {
            // If reverse geocoding fails, still set the location with coordinates
            const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            setSelectedLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
            setMapZoom(15);
            onLocationChange(lat, lng, address);
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          // If reverse geocoding fails, still set the location with coordinates
          const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          setSelectedLocation({ lat, lng, address });
          setMapCenter([lat, lng]);
          setMapZoom(15);
          onLocationChange(lat, lng, address);
        }

        setIsLoading(false);
        setIsGettingLocation(false);
      },
      (error) => {
        // Clear the watch on error
        navigator.geolocation.clearWatch(watchId);
        setIsGettingLocation(false);

        console.error("Geolocation error:", error);
        let errorMessage = "Unable to get your location. ";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage +=
              "Please allow location access in your browser settings.";
            console.error("PERMISSION_DENIED");
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location information is unavailable.";
            console.error("POSITION_UNAVAILABLE");
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out.";
            console.error("TIMEOUT");
            break;
          default:
            errorMessage += "An unknown error occurred.";
            console.error("Unknown error:", error);
            break;
        }
        console.error(errorMessage);
      },
      {
        enableHighAccuracy: false, // Start with false for faster response
        timeout: 30000, // 30 seconds
        maximumAge: 60000, // Accept cached location up to 1 minute old
      },
    );

    // Fallback timeout to clear watch if it takes too long
    setTimeout(() => {
      navigator.geolocation.clearWatch(watchId);
      if (isGettingLocation) {
        setIsGettingLocation(false);
        console.error("Geolocation watch timeout - clearing watch");
      }
    }, 35000); // 35 seconds total timeout
  }, [isWithinBounds, cityName, onLocationChange, isGettingLocation]);

  return (
    <div className={styles.map_picker}>
      <div className={styles.search_container}>
        <div className={styles.search_input_wrapper}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.search_input}
            placeholder={
              t("map.search_placeholder") || "Search for an address..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowResults(true);
              }
            }}
            disabled={disabled || !cityName}
          />
          {isLoading && (
            <div className={styles.loading_indicator}>
              {t("map.searching") || "Searching..."}
            </div>
          )}
          {showResults && searchResults.length > 0 && (
            <div ref={resultsRef} className={styles.search_results}>
              {searchResults.map((result) => (
                <div
                  key={result.place_id}
                  className={styles.result_item}
                  onClick={() => handleSelectResult(result)}
                >
                  <div className={styles.result_name}>
                    {result.display_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.current_location_btn}
          onClick={handleGetCurrentLocation}
          disabled={disabled || isGettingLocation || isLoading || !cityName}
          title={t("map.use_current_location") || "Use your current location"}
        >
          {isGettingLocation ? (
            <>
              <span className={styles.btn_icon}>📍</span>
              <span>{t("map.getting_location") || "Getting location..."}</span>
            </>
          ) : (
            <>
              <span className={styles.btn_icon}>📍</span>
              <span>
                {t("map.use_current_location") || "Use Current Location"}
              </span>
            </>
          )}
        </button>
      </div>

      <div className={styles.map_container}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
          maxBounds={mapBounds || undefined}
          maxBoundsViscosity={1.0}
          key={`${cityName}-${countryName}`} // Force remount when city changes
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewUpdater center={mapCenter} zoom={mapZoom} />
          {selectedLocation && (
            <Marker position={[selectedLocation.lat, selectedLocation.lng]} />
          )}
          <MapClickHandler
            onMapClick={handleMapClick}
            disabled={disabled || !cityName}
          />
        </MapContainer>
      </div>

      {selectedLocation && (
        <div className={styles.selected_info}>
          <div className={styles.info_label}>
            {t("map.selected_location") || "Selected Location:"}
          </div>
          <div className={styles.info_address}>{selectedLocation.address}</div>
          <div className={styles.info_coords}>
            {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLocationPicker;
